import styles from './ERModel.module.css';
import React, { useCallback, useEffect, useState } from 'react';
import {
  addEdge,
  Background,
  ConnectionMode,
  ControlButton,
  Controls,
  MarkerType,
  MiniMap,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import Icon from 'components/ui/common/Icon';
import UUID from 'bng/utils/UUID';

export const NODE_TYPES = { customNode: 'customNode', databaseTable: 'databaseTable' };

export default function ERModel({
  className = '',
  initialNodes = [],
  initialEdges = [],
  showControls = false,
  showMinimap = false,
  nodeTypes = {},
  edgeTypes = {},
  onNewNode = null,
  onNewEdge = null,
  showNewNodeButton = false,
  onSelectNode = _.noop,
  onChange = _.noop,
  children,
}) {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), [setEdges]);

  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes]);

  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  const onDrop = useCallback(
    (event) => {
      try {
        event.preventDefault();
        const data = JSON.parse(event.dataTransfer.getData('application/reactflow'));

        const position = reactFlowInstance.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });

        const newNode = {
          id: UUID.generate(),
          type: 'databaseTable',
          position,
          data: {
            tableInfo: {
              schema: data.schema,
              table: data.table,
              originType: data.originType,
              fields: data.fields,
            },
          },
        };

        onChange({ nodes: [...initialNodes, newNode], edges });
      } catch (e) {
        console.error('Error on onDrop: ', e);
      }
    },
    [reactFlowInstance, initialNodes]
  );

  const defaultEdgeOptions = {
    type: 'databaseEdge',
    style: { strokeWidth: 2, stroke: '#005dff' },
  };

  return (
    <div className={`ERModel ${styles.ERModel} ${className}`}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        minZoom={0.5}
        maxZoom={2}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={(params) =>
          onNewEdge
            ? onNewEdge({
                params,
                nodes,
                setNodes,
                edges,
                setEdges,
                reactFlowInstance,
              })
            : onConnect(params)
        }
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionMode={ConnectionMode.Loose}
        onNodeClick={onSelectNode}
        onInit={(instance) => {
          setReactFlowInstance(instance);
        }}
        onNodeDragStop={(event, node) => {
          const updatedNodes = nodes.map((n) => (n.id === node.id ? { ...n, position: node.position } : n));
          onChange({ nodes: updatedNodes, edges });
        }}
        onDragOver={onDragOver}
        onDrop={onDrop}
        defaultEdgeOptions={defaultEdgeOptions}
      >
        {showControls && (
          <Controls position={'top-right'} showInteractive={false}>
            {showNewNodeButton && (
              <ControlButton
                onClick={() => {
                  if (onNewNode) {
                    onNewNode({ nodes, setNodes, setEdges, reactFlowInstance });
                  } else {
                    const newNode = {
                      id: UUID.generate(),
                      type: 'Default',
                      position: { x: 0, y: nodes.length * 100 },
                      data: { label: nodes.length.toString() },
                    };
                    setNodes((nds) => nds.concat(newNode));
                  }
                }}
              >
                <Icon icon={'note_add'} size={20} className={styles.controlsIcon} />
              </ControlButton>
            )}
          </Controls>
        )}
        {showMinimap && <MiniMap />}
        <Background variant="dots" gap={12} size={1} />
        {_.isFunction(children) ? children({ nodes, setNodes, edges, setEdges, ...reactFlowInstance }) : children}
      </ReactFlow>
    </div>
  );
}
